import { Clue, CluedLetter } from "./clue";

const API_BASE = "https://state.shuntle.com";
const GAME_NAME = "shuntle";

export interface SerializedCluedLetter {
  letter: string;
  clue: number;
}

export interface RemoteGameState {
  user_id?: string;
  updated_at?: string;
  seconds_since_update?: number;
  metadata: {
    status: "in_progress" | "won" | "lost";
    numGuesses: number;
    puzzleId: string;
  };
  state: {
    guesses: { word: string; clues: SerializedCluedLetter[] }[];
    currentGuess: string;
    cursorPosition: number;
  };
}

export function serializeClues(clues: CluedLetter[]): SerializedCluedLetter[] {
  return clues.map((cl) => ({
    letter: cl.letter,
    clue: cl.clue ?? Clue.Absent,
  }));
}

export function deserializeClues(serialized: SerializedCluedLetter[]): CluedLetter[] {
  return serialized.map((s) => ({
    letter: s.letter,
    clue: s.clue as Clue,
  }));
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function saveGameState(
  userId: string,
  puzzleId: string,
  payload: RemoteGameState
): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    fetch(`${API_BASE}/state/${userId}/${GAME_NAME}/${puzzleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch((err) => {
      console.warn("Failed to sync game state:", err);
    });
  }, 300);
}

export async function fetchGameStateForPuzzle(
  puzzleId: string
): Promise<RemoteGameState | null> {
  try {
    const res = await fetch(`${API_BASE}/states/${GAME_NAME}?puzzle_id=${encodeURIComponent(puzzleId)}`);
    if (!res.ok) return null;
    const states = (await res.json()) as RemoteGameState[];
    if (!states || states.length === 0) return null;
    return states[0];
  } catch (err) {
    console.warn("Failed to fetch game state:", err);
    return null;
  }
}

export interface WatchableResult {
  kind: "live" | "replay";
  game: RemoteGameState;
}

export interface WatchableGames {
  liveGames: RemoteGameState[];
  replayGames: RemoteGameState[];
}

export async function findWatchableGames(
  puzzleId: string,
  excludeUserId: string
): Promise<WatchableGames> {
  const result: WatchableGames = { liveGames: [], replayGames: [] };
  try {
    // Fetch live games (in_progress, updated within 40s, prioritize fewer guesses)
    const liveRes = await fetch(
      `${API_BASE}/states/${GAME_NAME}?puzzle_id=${encodeURIComponent(puzzleId)}&max_age=40&status=in_progress&limit=100`
    );
    if (liveRes.ok) {
      const liveStates = (await liveRes.json()) as RemoteGameState[];
      if (liveStates) {
        result.liveGames = liveStates
          .filter((s) => s.user_id !== excludeUserId)
          .sort((a, b) => a.metadata.numGuesses - b.metadata.numGuesses);
      }
    }

    // Fetch replay games (completed)
    const replayRes = await fetch(
      `${API_BASE}/states/${GAME_NAME}?puzzle_id=${encodeURIComponent(puzzleId)}&limit=100`
    );
    if (replayRes.ok) {
      const replayStates = (await replayRes.json()) as RemoteGameState[];
      if (replayStates) {
        result.replayGames = replayStates.filter(
          (s) => s.user_id !== excludeUserId && (s.metadata.status === "won" || s.metadata.status === "lost")
        );
      }
    }
  } catch (err) {
    console.warn("Failed to find watchable games:", err);
  }
  return result;
}

export async function longPollGameState(
  userId: string,
  puzzleId: string,
  after: string | null,
  signal?: AbortSignal
): Promise<RemoteGameState | null> {
  try {
    const params = new URLSearchParams({ watch: "true", timeout: "20" });
    if (after) params.set("after", after);
    const res = await fetch(
      `${API_BASE}/state/${userId}/${GAME_NAME}/${puzzleId}?${params}`,
      { signal }
    );
    if (res.status === 304) return null;
    if (!res.ok) return null;
    return (await res.json()) as RemoteGameState;
  } catch (err) {
    if (signal?.aborted) return null;
    console.warn("Long-poll failed:", err);
    return null;
  }
}
